import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../firebase';
import StarRating from '../components/StarRating';
import styles from './ProfilePage.module.css';
import anonymousAvatar from '../assets/images/anonymous-avatar.svg';
import { getDisplayLocation } from '../utils/locationHelpers';

function ProfilePage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    const fetchUserData = async () => {
      setLoading(true);
      try {
        // Récupérer les favoris
        const favoritesQuery = query(
          collection(db, 'favorites'),
          where('userId', '==', currentUser.uid),
        );
        const favoritesSnapshot = await getDocs(favoritesQuery);
        const favoriteIds = favoritesSnapshot.docs.map((doc) => doc.data().hairstylistId);

        // Récupérer les données des coiffeuses favorites
        const favoritesData = await Promise.all(
          favoriteIds.map(async (id) => {
            const stylistDoc = await getDoc(doc(db, 'hairstylists', id));
            return stylistDoc.exists() ? { id: stylistDoc.id, ...stylistDoc.data() } : null;
          }),
        );
        setFavorites(favoritesData.filter(Boolean));

        // Récupérer les reviews de l'utilisateur
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('userId', '==', currentUser.uid),
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);
        const reviewsData = reviewsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Récupérer les données des coiffeuses notées
        const reviewsWithStylists = await Promise.all(
          reviewsData.map(async (review) => {
            const stylistDoc = await getDoc(doc(db, 'hairstylists', review.hairstylistId));
            return {
              ...review,
              stylist: stylistDoc.exists() ? { id: stylistDoc.id, ...stylistDoc.data() } : null,
            };
          }),
        );
        setReviews(reviewsWithStylists.filter((r) => r.stylist));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser, navigate]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Chargement...</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.title}>Mon compte</h1>
        <p className={styles.subtitle}>
          {currentUser?.displayName || currentUser?.email?.split('@')[0]}
        </p>

        {/* Favoris */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Mes favoris ({favorites.length})</h2>
          {favorites.length > 0 ? (
            <div className={styles.grid}>
              {favorites.map((stylist) => (
                <div
                  key={stylist.id}
                  className={styles.card}
                  onClick={() => navigate(`/search?stylist=${stylist.id}`)}
                >
                  <img
                    src={stylist.profilePhoto || anonymousAvatar}
                    alt={stylist.name}
                    className={styles.cardImage}
                  />
                  <div className={styles.cardContent}>
                    <h3 className={styles.cardName}>{stylist.name}</h3>
                    <p className={styles.cardCity}>{getDisplayLocation(stylist)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.empty}>Aucun favori pour le moment</p>
          )}
        </section>

        {/* Reviews */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Mes notes ({reviews.length})</h2>
          {reviews.length > 0 ? (
            <div className={styles.reviewsList}>
              {reviews.map((review) => (
                <div key={review.id} className={styles.reviewCard}>
                  <div className={styles.reviewHeader}>
                    <div className={styles.reviewStylist}>
                      <img
                        src={review.stylist.profilePhoto || 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&w=200&q=80'}
                        alt={review.stylist.name}
                        className={styles.reviewImage}
                      />
                      <div>
                        <h3 className={styles.reviewName}>{review.stylist.name}</h3>
                        <p className={styles.reviewCity}>{getDisplayLocation(review.stylist)}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles.viewBtn}
                      onClick={() => navigate(`/search?stylist=${review.stylist.id}`)}
                    >
                      Voir
                    </button>
                  </div>
                  <div className={styles.reviewRating}>
                    <StarRating rating={review.rating} reviewCount={0} />
                  </div>
                  {review.comment && (
                    <p className={styles.reviewComment}>{review.comment}</p>
                  )}
                  {review.createdAt && (
                    <p className={styles.reviewDate}>
                      {review.createdAt.toDate?.().toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }) || 'Date inconnue'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.empty}>Aucune note pour le moment</p>
          )}
        </section>
      </div>
    </div>
  );
}

export default ProfilePage;

